import { signIn } from "next-auth/react";
import type { SignInResponse } from "next-auth/react";

let activePatchedFetch: typeof fetch | null = null;
let activePatchedFetchRefCount = 0;

function getRequestUrl(input: Parameters<typeof fetch>[0]): string {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  if (input instanceof Request) {
    return input.url;
  }

  return "";
}

/**
 * NextAuth's signIn() throws when the callback endpoint returns a non-standard
 * HTTP status (e.g. 429 from a rate limiter), losing the status code info.
 * This wrapper captures the callback response status so we can detect 429.
 */
export async function credentialSignIn(
  credentials: Record<string, any>,
): Promise<SignInResponse | undefined> {
  if (typeof window === "undefined") {
    throw new Error("credentialSignIn must be called in the browser");
  }

  let callbackStatus: number | undefined;
  const nativeFetch = window.fetch.bind(window);
  const patchedFetch: typeof fetch = async (...args) => {
    const response = await nativeFetch(...args);
    const url = getRequestUrl(args[0]);
    if (url.includes("/api/auth/callback/")) {
      callbackStatus = response.status;
    }
    return response;
  };

  activePatchedFetch = patchedFetch;
  activePatchedFetchRefCount += 1;
  window.fetch = patchedFetch;

  try {
    return await signIn("credentials", { redirect: false, ...credentials });
  } catch {
    if (callbackStatus === 429) {
      return { error: "TOO_MANY_ATTEMPTS", status: 429, ok: false, url: null };
    }
    throw new Error("An unexpected error occurred");
  } finally {
    activePatchedFetchRefCount -= 1;
    if (activePatchedFetchRefCount <= 0 && window.fetch === activePatchedFetch) {
      window.fetch = nativeFetch;
      activePatchedFetch = null;
      activePatchedFetchRefCount = 0;
    }
  }
}
